import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { Layout, Spin, Typography, Button, Space, Dropdown, Menu } from 'antd';
import { UserOutlined, LogoutOutlined, FormOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Suspense, lazy } from 'react';

import { AuthProvider, useAuth } from '@/auth/auth-context';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const { Header, Content, Footer } = Layout;

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SubmissionForm = lazy(() => import('./pages/submission/SubmissionForm'));
const SubmissionDetail = lazy(() => import('./pages/submission/SubmissionDetail'));
const SubmissionList = lazy(() => import('./pages/status/SubmissionList'));
const SharePage = lazy(() => import('./pages/share/SharePage'));
const PaymentPage = lazy(() => import('./pages/pay/PaymentPage'));
const TrackOrderPage = lazy(() => import('./pages/track/TrackOrderPage'));
const AcceptancePage = lazy(() => import('./pages/install/AcceptancePage'));
const UpdateRequestList = lazy(() => import('./pages/update-requests/UpdateRequestList'));
const UpdateRequestForm = lazy(() => import('./pages/update-requests/UpdateRequestForm'));

const Loading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Spin size="large" tip={t('loading')} />
    </div>
  );
};

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
};

const GuestRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <Loading />;
  if (isAuthenticated) return <Navigate to="/submissions" replace />;
  return <Outlet />;
};

const AppHeader: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const selectedKey = location.pathname.startsWith('/update-requests') ? 'updates' : 'submissions';

  return (
    <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#001529', padding: '0 24px' }}>
      <Space size={24}>
        <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
          {t('appTitle')}
        </Typography.Title>
        {isAuthenticated && (
          <Menu
            mode="horizontal"
            selectedKeys={[selectedKey]}
            theme="dark"
            style={{ background: 'transparent', borderBottom: 'none', minWidth: 320 }}
            items={[
              {
                key: 'submissions',
                icon: <FileTextOutlined />,
                label: <Link to="/submissions">{t('nav.submissions', 'My Submissions')}</Link>,
              },
              {
                key: 'updates',
                icon: <FormOutlined />,
                label: <Link to="/update-requests">{t('nav.updateRequests', 'Update Requests')}</Link>,
              },
            ]}
          />
        )}
      </Space>
      <Space>
        <LanguageSwitcher />
        {isAuthenticated && user && (
          <Dropdown
            menu={{
              items: [
                { key: 'user', label: user.email, disabled: true },
                { type: 'divider' },
                { key: 'logout', label: t('signOut'), icon: <LogoutOutlined />, onClick: logout },
              ],
            }}
          >
            <Button type="text" style={{ color: '#fff' }}>
              <Space>
                <UserOutlined />
                {user.displayName}
              </Space>
            </Button>
          </Dropdown>
        )}
      </Space>
    </Header>
  );
};

export const App: React.FC = () => {
  const { t } = useTranslation();
  return (
    <BrowserRouter>
      <AuthProvider>
        <Layout style={{ minHeight: '100vh' }}>
          <AppHeader />
          <Content style={{ padding: '24px 48px' }}>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="share/:token" element={<SharePage />} />
                <Route path="pay/:id" element={<PaymentPage />} />
                <Route path="track" element={<TrackOrderPage />} />
                <Route path="install/accept/:token" element={<AcceptancePage />} />
                <Route element={<GuestRoute />}>
                  <Route path="login" element={<LoginPage />} />
                </Route>
                <Route element={<ProtectedRoute />}>
                  <Route index element={<Navigate to="/submissions" replace />} />
                  <Route path="submissions" element={<SubmissionList />} />
                  <Route path="submissions/new" element={<SubmissionForm />} />
                  <Route path="submissions/:id" element={<SubmissionDetail />} />
                  <Route path="submissions/:id/edit" element={<SubmissionForm />} />
                  <Route path="update-requests" element={<UpdateRequestList />} />
                  <Route path="update-requests/new" element={<UpdateRequestForm />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Content>
          <Footer style={{ textAlign: 'center' }}>
            {t('appFooter')} &copy; {new Date().getFullYear()}
          </Footer>
        </Layout>
      </AuthProvider>
    </BrowserRouter>
  );
};
